import Button from "core/components/Button";
import Modal from "core/components/Modal";
import Search from "core/components/Field/Search";
import { useMemo, useState } from "react";
import Pill from "core/components/Pill";
import {
  deepMergeObjects,
  COMPONENTS,
  UPDATED_COMPONENTS,
  compareObjects,
  getFilteredReviewFields,
} from "../utilities.jsx";
import { SkeletonAccordion } from "./UtilityComponents.jsx";
import { defaultLicenseState, FIELDS } from "../constants.js";
import {
  Card,
  ProductBundle,
  Product,
  SectionSeparator,
  ProductHeader,
  ProductAttributes,
  CurrentChanges,
  Separator,
  NewChanges,
  ModalHeader,
  List,
  ListItem,
  ProductTitle,
  SeparatorIcon,
} from "../styles.js";
import ButtonLink from "core/components/ButtonLink";
import Typography from "core/components/Typography";

const ReviewDraftChanges = ({
  bundles,
  products,
  productsEnabled,
  getDynamicProduct,
  isLoading,
  previousData,
}) => {
  const [moreTitle, setMoreTitle] = useState(null);
  const [moreItems, setMoreItems] = useState(null);
  const [moreSearch, setMoreSearch] = useState("");

  const modalItems = useMemo(() => {
    return moreItems && moreSearch
      ? moreItems.filter((item) =>
          (item.label || item).toLowerCase().includes(moreSearch.toLowerCase())
        )
      : moreItems;
  }, [moreItems, moreSearch]);

  const showMoreItems = (title, items) => {
    setMoreTitle(title);
    setMoreItems(items);
  };

  const hideMoreItems = () => {
    setMoreTitle(null);
    setMoreItems(null);
    setMoreSearch("");
  };

  if (isLoading) {
    return <SkeletonAccordion itemCount={bundles.length} />;
  }

  return (
    <div data-test="review-draft">
      {Object.values(bundles).map((bundle) => {
        const bundleProducts = Object.values(products).filter(
          (product) => product.meta.bundle.id === bundle.id
        );

        return (
          <Card key={bundle.id} value={bundle.id} title={bundle.name}>
            <ProductBundle style={{ height: 29 }}> {bundle.name}</ProductBundle>

            {bundleProducts.map((product) => {
              const dynamicProduct = getDynamicProduct(product.id);
              const reviewFields = FIELDS[bundle.id]?.[product.id];
              const enabled = productsEnabled.includes(product.id);
              const previouslyEnabled = previousData[product.id].enabled;
              const productLink = `../${bundle.path}/${product.page.path}`;

              const formattedDynamicProduct = deepMergeObjects(
                defaultLicenseState[product.id],
                dynamicProduct?.data
              );

              const newData = {
                ...dynamicProduct,
                data: formattedDynamicProduct,
              };

              const updatedData = compareObjects(
                previousData[product.id],
                newData.data
              );

              const { extractedFields, filteredReviewFields } =
                getFilteredReviewFields(updatedData, product.id, reviewFields);

              const showLicenseStatus = previouslyEnabled !== enabled;

              return (
                <Product key={product.id}>
                  <SectionSeparator />
                  <ProductHeader>
                    <ProductTitle>{product.meta.name}</ProductTitle>
                    <ButtonLink to={productLink} variant="text" size="small">
                      Edit
                    </ButtonLink>
                  </ProductHeader>
                  <ProductAttributes>
                    <CurrentChanges>
                      <Pill
                        text="Current"
                        size="small"
                        variant="timeout"
                        style={{ display: "inline" }}
                      />
                      <div style={{ marginTop: 8 }}>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 8,
                          }}
                        >
                          {showLicenseStatus && (
                            <span>
                              {previouslyEnabled ? "Licensed" : "Not Licensed"}
                            </span>
                          )}
                          {filteredReviewFields &&
                            Object.keys(filteredReviewFields).map((key) => {
                              const Component = COMPONENTS[key];

                              return (
                                <div key={key} style={{ paddingBottom: 10 }}>
                                  <Component
                                    product={newData}
                                    reviewFieldKey={filteredReviewFields[key]}
                                    showMoreItems={showMoreItems}
                                    previousData={previousData[product.id]}
                                    showNewChanges={false}
                                    extractedFields={
                                      extractedFields[product.id]
                                    }
                                  />
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    </CurrentChanges>
                    <Separator>
                      <SeparatorIcon />
                    </Separator>
                    <NewChanges>
                      <Pill
                        text="Update"
                        size="small"
                        variant="success"
                        style={{ display: "inline" }}
                      />
                      <div style={{ marginTop: 8 }}>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 8,
                          }}
                        >
                          {showLicenseStatus && (
                            <span>{enabled ? "Licensed" : "Not Licensed"}</span>
                          )}
                          {filteredReviewFields &&
                            Object.keys(filteredReviewFields).map((key) => {
                              const Component = [
                                "attributes",
                                "states",
                                "zipcodes",
                                "models",
                                "query",
                              ].includes(key)
                                ? UPDATED_COMPONENTS[key]
                                : COMPONENTS[key];

                              return (
                                <div key={key} style={{ paddingBottom: 10 }}>
                                  <Component
                                    product={newData}
                                    reviewFieldKey={filteredReviewFields[key]}
                                    showMoreItems={showMoreItems}
                                    showNewChanges={true}
                                    updatedData={updatedData}
                                  />
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    </NewChanges>
                  </ProductAttributes>
                </Product>
              );
            })}
          </Card>
        );
      })}

      <Modal visible={!!moreItems} dismiss={hideMoreItems}>
        <ModalHeader>{moreTitle}</ModalHeader>
        <Search
          styles={{ container: { width: "100%", marginBottom: "15px" } }}
          value={moreSearch}
          onChange={(value) => setMoreSearch(value)}
        />

        <List>
          {modalItems?.map((item, index) => {
            return (
              <ListItem key={index} index={index}>
                {item.label || item}
              </ListItem>
            );
          })}
        </List>

        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "end",
          }}
        >
          <Button variant="outlined" width="143px" onClick={hideMoreItems}>
            Close
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default ReviewDraftChanges;
